import { router } from "@/router";
import useAuthStore from "@/store/authStore";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RouterProvider } from "react-router-dom";
import { ErrorFallback, PageLoader, Toaster } from "./components/ui";
import { TooltipProvider } from "./components/ui/tooltip";
import { CanvasProvider } from "./contexts/CanvasContext";
import { ThemeProvider } from "./contexts/ThemeProvider";
import { useInputModality } from "./hooks/useInputModality";
import { useVersionPolling } from "./hooks/useVersionPolling";
import { preloadGradientAvatars } from "./lib/db/utils/supabase/storage/randomAvatars";
import { supabase } from "./lib/supabase";
import "./styles/message-content.css";

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  "/auth",
  "/pricing",
  "/features",
  "/about",
  "/blog",
  "/changelog",
  "/news",
  "/privacy",
  "/terms",
  "/contact",
  "/help",
  "/support",
  "/docs",
  "/shared", // Shared content pages
  "/temp-chat", // Temporary chat (no auth required)
  "/test", // Test routes (dev-only, excluded from production builds)
];

/**
 * Check if the current path is a public route that doesn't require session
 */
function isPublicRoute(pathname) {
  // Root path is public (landing page)
  if (pathname === "/" || pathname === "") {
    return true;
  }
  // Check if path starts with any public route prefix
  return PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * Early session check - runs before React state to catch invalid sessions immediately
 * This is a synchronous check of local storage, not a server call
 */
function useEarlySessionCheck() {
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const pathname = window.location.pathname;

      // Skip session check on public routes - they don't require auth
      if (isPublicRoute(pathname)) {
        setSessionChecked(true);
        return;
      }

      // Quick local session check for protected routes
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        // No local session - clear any stale Zustand state and redirect
        console.warn("[App] No valid session found, redirecting to login");
        localStorage.removeItem("auth-storage"); // Clear stale Zustand state
        window.location.href = "/auth/login?reason=session_expired";
        return;
      }

      setSessionChecked(true);
    };

    checkSession();
  }, []);

  return sessionChecked;
}

function AppContent() {
  // Run early session check before anything else
  const sessionChecked = useEarlySessionCheck();

  // Check for new deployments and show update toast
  useVersionPolling();

  const authState = useAuthStore();

  // Safely check if isAuthenticated is a function, then call it, or fall back to checking user directly
  const isAuthenticated =
    typeof authState.isAuthenticated === "function"
      ? authState.isAuthenticated()
      : !!authState.user;
  const profile = authState.profile;
  const initialized = authState.initialized;
  const isLoading = authState.isLoading;
  const initialize = authState.initialize;

  useEffect(() => {
    // Initialize auth state if not already initialized
    if (!initialized && !isLoading) {
      initialize();
    }
  }, [initialized, isLoading, initialize]);

  // Listen for auth state changes (session expired, signed out elsewhere, etc.)
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Handle session expiration or sign out
      if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session)) {
        // Only redirect if not already on auth pages
        if (!window.location.pathname.startsWith("/auth")) {
          // Check if this is an intentional logout (not a session expiration)
          const isIntentionalLogout =
            localStorage.getItem("intentional_logout");
          if (isIntentionalLogout) {
            localStorage.removeItem("intentional_logout");
            // Intentional logout — nav-user.jsx handles the redirect to "/"
            return;
          }
          console.warn(
            "[Auth] Session expired or signed out, redirecting to login"
          );
          window.location.href = "/auth/login?reason=session_expired";
        }
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Handle OAuth redirect first
    const urlParams = new URLSearchParams(window.location.search);
    const oauthCode = urlParams.get("code");

    if (oauthCode) {
      return; // Let the router handle OAuth callback
    }

    // If user is authenticated with profile and on root path, immediately redirect to chat
    if (initialized && isAuthenticated && profile) {
      const currentPath = window.location.pathname;
      if (currentPath === "/" || currentPath === "") {
        window.location.replace("/chat");
        return;
      }
    }
  }, [initialized, isAuthenticated, profile]);

  // Wait for session check to complete before rendering
  if (!sessionChecked) {
    return <PageLoader message="Checking session..." />;
  }

  // Show loading state while auth is initializing
  if (!initialized || isLoading) {
    return <PageLoader message="Loading..." />;
  }

  // Handle OAuth redirect case
  const urlParams = new URLSearchParams(window.location.search);
  const oauthCode = urlParams.get("code");

  if (oauthCode) {
    return <RouterProvider router={router} />;
  }

  // If user is authenticated with profile and on root path, show loading while redirecting
  if (initialized && isAuthenticated && profile) {
    const currentPath = window.location.pathname;
    if (currentPath === "/" || currentPath === "") {
      return <PageLoader message="Redirecting to chat..." />;
    }
  }

  // For all other cases, use the normal router
  return <RouterProvider router={router} />;
}

export default function App() {
  // Track keyboard vs mouse input for focus ring visibility
  useInputModality();

  useEffect(() => {
    // Performance optimization: Remove loading states
    const initialLoader = document.querySelector(".initial-loader");
    if (initialLoader) {
      initialLoader.style.display = "none";
    }

    document.documentElement.classList.remove("loading");

    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.classList.add("js-loading-complete");
    }

    if (window.markAppReady) {
      window.markAppReady();
    }

    // Performance metrics
    if (window.performanceMetrics) {
      window.performanceMetrics.markMetric("App Component Mounted");
    }

    // Preload gradient avatars for better performance (non-blocking)
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(
        () => {
          preloadGradientAvatars();
        },
        { timeout: 1000 }
      );
    } else {
      // Fallback for environments without requestIdleCallback
      setTimeout(() => {
        preloadGradientAvatars();
      }, 100);
    }

    // Restore console functionality after app loads
    if (window.restoreConsole) {
      setTimeout(window.restoreConsole, 1000);
    }
  }, []);

  return (
    <ThemeProvider>
      <TooltipProvider>
        <CanvasProvider>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Toaster />
            <AppContent />
          </ErrorBoundary>
        </CanvasProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
